import { Outlet, useMatches } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { Session, Space } from '@product-portal/shared';
import { AppHeader } from '@/components/portal/app-header';
import { apiFetch, isApiError } from '@/lib/api';
import { useQueueCount } from '@/hooks/use-product';
import { cn } from '@/lib/utils';
import { AccessDeniedPage } from '@/pages/access-denied-page';
import { LoadingScreen } from '@/pages/loading-screen';

/** Route handle: `{ wide: true }` widens the container to 1392px (screen 07 only). */
export interface RouteHandle {
  wide?: boolean;
}

function useWide(): boolean {
  return useMatches().some((m) => (m.handle as RouteHandle | undefined)?.wide === true);
}

function Shell({ space, session }: { space: Space; session: Session }) {
  const wide = useWide();
  // Only rendered once the API accepted the product space (PO).
  const toQualify = useQueueCount(space === 'product');
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader space={space} session={session} wide={wide} badges={{ '/produit/qualification': toQualify.data }} />
      <main className={cn('mx-auto box-border flex w-full flex-col gap-7 px-6 pb-12 pt-8', wide ? 'max-w-[1392px]' : 'max-w-[1200px]')}>
        {/* Pages read the session from the outlet context (useOutletContext). */}
        <Outlet context={session} />
      </main>
    </div>
  );
}

/** Espace métier: every signed-in user attached to a team. */
export function BusinessLayout({ session }: { session: Session }) {
  return <Shell space="business" session={session} />;
}

/**
 * Espaces produit et admin. The shell renders only once the API has accepted
 * the role (GET /api/<space>/context, PO-only server-side). A role check in
 * the router alone would hide the screen and leave the route open.
 */
export function GuardedSpaceLayout({ space, session }: { space: Exclude<Space, 'business'>; session: Session }) {
  const context = useQuery({
    queryKey: ['space-context', space],
    queryFn: () => apiFetch<{ space: Space }>(`/api/${space}/context`),
    retry: false,
  });
  if (context.isPending) return <LoadingScreen />;
  if (context.isError) {
    if (isApiError(context.error, 401)) {
      window.location.assign('/connexion');
      return <LoadingScreen />;
    }
    return <AccessDeniedPage session={session} reason={isApiError(context.error, 403, 'NO_TEAM') ? 'no_team' : 'forbidden'} />;
  }
  return <Shell space={space} session={session} />;
}
